import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Bell, BellRing, Sparkles, Users, Rocket, MessageSquare, MessageCircle, Building2, Trophy, Check, UserPlus, Calendar, Radar, LifeBuoy, GraduationCap, Landmark, Info, ShieldAlert } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/use-auth';
import { usePush } from '@/hooks/use-push';

const kindMeta = {
  ai:        { icon: Sparkles,      bg: 'ai-surface',                          label: 'AI',        ai: true },
  mentor:    { icon: Users,         bg: 'bg-plum text-plum-foreground',        label: 'Mentor' },
  venture:   { icon: Rocket,        bg: 'bg-primary text-primary-foreground',  label: 'Venture' },
  community: { icon: MessageSquare, bg: 'bg-teal text-teal-foreground',        label: 'Community' },
  investor:  { icon: Building2,     bg: 'bg-success text-success-foreground',  label: 'Investor' },
  score:     { icon: Trophy,        bg: 'bg-info text-info-foreground',        label: 'Score' },
  // Direct messages get their own identity — distinct from connection
  // requests (network) so the two are never confused in the feed.
  messaging: { icon: MessageCircle, bg: 'bg-sky-600 text-white',               label: 'Message' },
  dm:        { icon: MessageCircle, bg: 'bg-sky-600 text-white',               label: 'Message' },
  network:   { icon: UserPlus,      bg: 'bg-rose text-rose-foreground',        label: 'Connection' },
  event:     { icon: Calendar,      bg: 'bg-gold text-gold-foreground',        label: 'Event' },
  session:   { icon: Calendar,      bg: 'bg-gold text-gold-foreground',        label: 'Session' },
  opportunity: { icon: Radar,       bg: 'bg-plum text-plum-foreground',        label: 'Opportunity' },
  capital:   { icon: Landmark,      bg: 'bg-success text-success-foreground',  label: 'Capital' },
  support:   { icon: LifeBuoy,      bg: 'bg-warning text-white',                label: 'Support' },
  learning:  { icon: GraduationCap, bg: 'bg-info text-info-foreground',        label: 'Learning' },
  lms:       { icon: GraduationCap, bg: 'bg-info text-info-foreground',        label: 'Learning' },
  moderation: { icon: ShieldAlert,  bg: 'bg-destructive text-destructive-foreground', label: 'Safety' },
  admin:     { icon: Info,          bg: 'bg-secondary text-foreground',        label: 'Admin' },
  system:    { icon: Info,          bg: 'bg-secondary text-foreground',        label: 'System' },
};
const DEFAULT_KIND = { icon: Bell, bg: 'bg-secondary text-foreground', label: 'Update' };

// Normalise server notif → UI shape used below
const norm = (n) => ({ ...n, unread: !n.read });

export const NotificationsBell = () => {
  const { token, API } = useAuth();
  const navigate = useNavigate();
  const { enable, configured, deviceEnabled } = usePush();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [enabling, setEnabling] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const unread = items.filter((i) => i.unread).length;

  // Pull live notifications from backend (no mock fallback)
  useEffect(() => {
    if (!token) return;
    fetch(`${API}/api/notifications/`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { setItems((d?.notifications || []).map(norm)); })
      .catch(() => {});
  }, [token, API]);

  const markAll = async () => {
    setItems((s) => s.map((i) => ({ ...i, unread: false })));
    if (token) {
      try { await fetch(`${API}/api/notifications/read-all`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }); } catch {}
    }
  };
  const markOne = async (id) => {
    setItems((s) => s.map((i) => (i.id === id ? { ...i, unread: false } : i)));
    if (token) {
      try { await fetch(`${API}/api/notifications/${id}/read`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } }); } catch {}
    }
  };

  const openNotification = (n) => {
    markOne(n.id);
    const link = n.link || '';
    if (link) {
      setOpen(false);
      if (/^https?:\/\//i.test(link)) { window.open(link, '_blank', 'noopener'); return; }
      navigate(link);
    }
  };

  const enablePush = async () => {
    if (!configured) { toast.error('Push isn\u2019t configured yet'); return; }
    setEnabling(true);
    try {
      await enable();
      await fetch(`${API}/api/notifications/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ push_enabled: true }),
      });
      toast.success('Push enabled on this device');
    } catch (e) { toast.error('Could not enable push', { description: e.message }); }
    finally { setEnabling(false); }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-9 w-9" data-testid="notifications-bell" aria-label="Notifications">
          {unread > 0 ? <BellRing className="h-4 w-4" /> : <Bell className="h-4 w-4" />}
          {unread > 0 && (
            <span className="absolute top-1 right-1 h-4 min-w-[16px] px-1 rounded-full bg-rose text-rose-foreground text-[10px] font-medium grid place-items-center">
              {unread}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md p-0 flex flex-col">
        <SheetHeader className="px-5 py-4 border-b border-border flex-row items-center justify-between space-y-0">
          <div>
            <SheetTitle className="font-display text-2xl">Notifications</SheetTitle>
            <p className="text-xs text-muted-foreground mt-0.5">{unread} unread · {items.length} total</p>
          </div>
          {unread > 0 && (
            <Button variant="ghost" size="sm" onClick={markAll} className="text-xs" data-testid="notif-mark-all">
              <Check className="h-3 w-3 mr-1" /> Mark all read
            </Button>
          )}
        </SheetHeader>

        {/* Push registration — same per-device web-push state as Settings */}
        {configured && !deviceEnabled && !dismissed && (
          <div className="m-4 rounded-xl border border-dashed border-primary/40 bg-primary/[0.06] p-4">
            <div className="flex items-start gap-3">
              <BellRing className="h-4 w-4 text-primary mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-medium">Get notified on this device</p>
                <p className="text-xs text-muted-foreground mt-1">Enable push for mentor confirmations, MVP deadlines, and Copilot nudges. You{`'`}ll need to enable it again on each new device.</p>
                <div className="flex gap-2 mt-3">
                  <Button size="sm" onClick={enablePush} disabled={enabling} className="rounded-full h-8 bg-primary text-primary-foreground hover:bg-primary/90" data-testid="notif-enable-push">
                    {enabling ? 'Enabling…' : 'Enable push'}
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setDismissed(true)} className="rounded-full h-8 text-xs">Not now</Button>
                </div>
              </div>
            </div>
          </div>
        )}
        {configured && deviceEnabled && (
          <div className="m-4 rounded-xl border border-success/30 bg-success/10 p-3 flex items-center gap-2">
            <Check className="h-4 w-4 text-success" />
            <p className="text-xs">Push enabled on this device. We{`'`}ll only ping for high-signal updates.</p>
          </div>
        )}

        <ul className="flex-1 overflow-y-auto divide-y divide-border">
          {items.map((n) => {
            const meta = kindMeta[n.kind] || DEFAULT_KIND;
            const Icon = meta.icon;
            return (
              <li key={n.id} data-testid={`notif-${n.id}`}>
                <button
                  onClick={() => openNotification(n)}
                  className={`w-full text-left flex items-start gap-3 px-5 py-4 transition-colors ${n.unread ? (meta.ai ? 'bg-accent/[0.06]' : 'bg-primary/[0.04]') : ''} hover:bg-secondary/60`}
                >
                  <div className={`h-9 w-9 rounded-lg ${meta.bg} grid place-items-center shrink-0`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      {meta.ai ? (
                        <span className="ai-chip">{meta.label}</span>
                      ) : (
                        <Badge variant="outline" className="border-border text-[9px] font-grotesk uppercase tracking-[0.16em]">{meta.label}</Badge>
                      )}
                      {n.unread && <span className={`h-1.5 w-1.5 rounded-full ${meta.ai ? 'bg-accent animate-ai-pulse' : 'bg-primary'}`} />}
                      <span className="ml-auto text-[10px] font-grotesk uppercase tracking-[0.16em] text-muted-foreground">{n.time}</span>
                    </div>
                    <p className={`text-sm font-medium mt-1.5 ${meta.ai && n.unread ? 'ai-text' : ''}`}>{n.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{n.body}</p>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      </SheetContent>
    </Sheet>
  );
};

export default NotificationsBell;
